/**
 * PoseDebugPage — /pose-debug
 *
 * Displays actual photos with COCO-17 keypoint skeleton overlaid on a
 * transparent canvas. Limbs are coloured by PoseCheckerV2 result.
 *
 * Image URL: /pose_images/{uuid}_{view}_img.jpeg  (1024×1820 px)
 * Keypoints: coco_kp_px — pixel coords in the 1024×1820 image space.
 *   The canvas internal resolution is set to 1024×1820 so coordinates
 *   map directly; CSS scales both image and canvas together.
 *
 * COCO-17 index order (fixture):
 *   0 nose  1 l_eye  2 r_eye  3 l_ear  4 r_ear
 *   5 l_shoulder  6 r_shoulder  7 l_elbow  8 r_elbow
 *   9 l_wrist  10 r_wrist  11 l_hip  12 r_hip
 *  13 l_knee  14 r_knee  15 l_ankle  16 r_ankle
 */

import { IonContent, IonPage } from "@ionic/react";
import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { checkFrontPoseV2, checkSidePoseV2, CheckResults } from "../components/PoseCheckerV2";
import {
  classifyPoseErrors,
  getVoiceMessage,
  PoseError,
  PoseErrorId,
  VoiceMessageId,
  VoiceMessage,
  ERROR_CATEGORY_ORDER,
  ERROR_CATEGORY_META,
  VOICE_MESSAGE_ORDER,
  VOICE_MESSAGE_META,
} from "../components/PoseErrorClassifier";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RotationData {
  uuid: string;
  view: string;
  body_rotation_deg?: number;
  hip_rotation_deg?: number;
  shoulder_width_m?: number;
  hip_width_m?: number;
  lSh_z?: number;
  rSh_z?: number;
  error?: string;
}

interface PoseSample {
  uuid: string;
  view: "front" | "right";
  l_arm_vert: number;
  r_arm_vert: number;
  l_leg_vert: number;
  r_leg_vert: number;
  l_elbow_ang: number;
  r_elbow_ang: number;
  torso_tilt_deg: number;
  fe_valid: boolean;
  fe_issues: string[];
  our_be_valid: boolean;
  our_be_issues: string[];
  pipeline_be_valid: boolean;
  pipeline_be_issues: string[];
  side_arm?: {
    dx_wrist_over_trunk: number;
    dx_elbow_over_trunk: number;
    inside_wrist: boolean;
    inside_elbow: boolean;
  };
  coco_kp_px: [number, number][];
  coco_kp_norm_bbox: [number, number][];
  camera_elevation_m?: number;
  torso_pitch_deg?: number;
}

type Category =
  | "inliers_sample"
  | "outliers_sample"
  | "fe_false_positive"
  | "fe_false_negative";

interface SamplesJson {
  front: Record<Category, PoseSample[]>;
  right: Record<Category, PoseSample[]>;
}

// ---------------------------------------------------------------------------
// COCO-17 → MediaPipe compact-array index map
// ---------------------------------------------------------------------------
const COCO_TO_MP_COMPACT: number[] = [
  0, 2, 1, 4, 3, 6, 5, 8, 7, 10, 9, 12, 11, 14, 13, 16, 15,
];
const MP_COMPACT_SIZE = 17;
const IMG_W = 1024;
const IMG_H = 1820;

function cocoNormToMpLandmarks(coco: [number, number][], W: number, H: number) {
  const compact = Array.from({ length: MP_COMPACT_SIZE }, () => ({
    x: 0, y: 0, z: 0, visibility: 0,
  }));
  coco.forEach(([nx, ny], i) => {
    const mpIdx = COCO_TO_MP_COMPACT[i];
    compact[mpIdx] = { x: nx * W, y: ny * H, z: 0, visibility: 1 };
  });
  return compact;
}

/** Build MP-compact landmarks directly from real pixel coordinates.
 * Preserves the true aspect ratio so angle checks are geometrically correct. */
function cocoKpPxToMpLandmarks(coco: [number, number][]) {
  const compact = Array.from({ length: MP_COMPACT_SIZE }, () => ({
    x: 0, y: 0, z: 0, visibility: 0,
  }));
  coco.forEach(([x, y], i) => {
    const mpIdx = COCO_TO_MP_COMPACT[i];
    compact[mpIdx] = { x, y, z: 0, visibility: 1 };
  });
  return compact;
}

// ---------------------------------------------------------------------------
// COCO-17 skeleton connections (COCO index pairs)
// ---------------------------------------------------------------------------
const COCO_SKELETON: [number, number][] = [
  [5, 6], [5, 7], [7, 9], [6, 8], [8, 10],
  [5, 11], [6, 12], [11, 12],
  [11, 13], [13, 15], [12, 14], [14, 16],
];

function getHeadAnchor(kpPx: [number, number][]): [number, number] {
  const headIdx = [0, 1, 2, 3, 4];
  const pts = headIdx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  if (pts.length === 0) return kpPx[0] ?? [0, 0];
  const sx = pts.reduce((acc, [x]) => acc + x, 0);
  const sy = pts.reduce((acc, [, y]) => acc + y, 0);
  return [sx / pts.length, sy / pts.length];
}

function getPoseBBox(kpPx: [number, number][]) {
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  const pts = idx
    .map((i) => kpPx[i])
    .filter((p): p is [number, number] => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const xs = pts.map(([x]) => x);
  const ys = pts.map(([, y]) => y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    minX,
    maxX,
    minY,
    maxY,
    width: Math.max(1, maxX - minX),
    height: Math.max(1, maxY - minY),
  };
}

function keypointsInsideGuidanceBox(
  kpPx: [number, number][],
  natW: number,
  natH: number,
  view: "front" | "right"
) {
  const marginH = 0.05;
  const marginW = view === "front" ? 0.05 : 0.25;
  const x0 = marginW * natW;
  const x1 = (1 - marginW) * natW;
  const y0 = marginH * natH;
  const y1 = (1 - marginH) * natH;
  const idx = [0, 5, 6, 9, 10, 11, 12, 15, 16];
  return idx.every((i) => {
    const p = kpPx[i];
    if (!p) return false;
    return p[0] >= x0 && p[0] <= x1 && p[1] >= y0 && p[1] <= y1;
  });
}

function drawFrontSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult: number = 1,
  torsoWidthMult: number = 1
) {
  const sx = scale * torsoWidthMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const roundedRectPath = (
    c: CanvasRenderingContext2D,
    x: number, y: number, w: number, h: number, r: number
  ) => {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    c.moveTo(x + rr, y);
    c.lineTo(x + w - rr, y);
    c.quadraticCurveTo(x + w, y, x + w, y + rr);
    c.lineTo(x + w, y + h - rr);
    c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    c.lineTo(x + rr, y + h);
    c.quadraticCurveTo(x, y + h, x, y + h - rr);
    c.lineTo(x, y + rr);
    c.quadraticCurveTo(x, y, x + rr, y);
  };

  // Head circle.
  ctx.beginPath();
  ctx.arc(headX, headY, hr, 0, Math.PI * 2);
  ctx.stroke();

  // data: shoulders are 98*sy below head anchor; hr=46 so neckGap=52.
  const bodyY = headY + hr + 18 * sy;

  // Torso — widened to 168*sx so its edges explicitly overlap the shoulder keypoints.
  const torsoW = 168 * sx;
  const torsoH = 214 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, headX - torsoW / 2, bodyY, torsoW, torsoH, 22 * sy);
  ctx.stroke();

  // Two legs — data: hip span=83*sy → each hip ±41*sx from centre, legH=321*sy.
  const legSpread = 41 * sx;
  const legW = 56 * sx;
  const legH = 321 * sy;
  const legY = bodyY + torsoH + 6 * sy;
  for (const side of [-1, 1]) {
    ctx.beginPath();
    roundedRectPath(ctx, headX + side * legSpread - legW / 2, legY, legW, legH, 18 * sy);
    ctx.stroke();
  }

  // Two arms — pivot at bodyY (shoulder level), angle 30° outward.
  // Shoulder coverage comes from the wide torso (±84*sx).
  const armOffX  = 60 * sx;
  const armW     = 62 * sx;
  const armH     = 290 * sy;
  const armAngle = 30 * (Math.PI / 180);
  for (const side of [-1, 1]) {
    ctx.save();
    ctx.translate(headX + side * armOffX, bodyY);
    ctx.rotate(-side * armAngle);
    ctx.beginPath();
    roundedRectPath(ctx, -armW / 2, 0, armW, armH, 18 * sy);
    ctx.stroke();
    ctx.restore();
  }
}

function drawSideSilhouette(
  ctx: CanvasRenderingContext2D,
  headX: number,
  headY: number,
  scale: number,
  color: string,
  headRadiusMult: number = 1,
  sideFatnessMult: number = 1,
  spineX?: number
) {
  const sx = scale * sideFatnessMult;
  const sy = scale;
  const hr = 46 * sy * headRadiusMult;
  // Face keypoints (nose/eyes/ears) are on the forward face, not the true head centre.
  // Shift the circle centre backward (left) so it represents the full skull.
  // The face keypoints remain inside the larger circle.
  const headCX = headX - hr * 0.38;
  const headCY = headY;

  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(3, 5 * scale);
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  const roundedRectPath = (
    c: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
  ) => {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    c.moveTo(x + rr, y);
    c.lineTo(x + w - rr, y);
    c.quadraticCurveTo(x + w, y, x + w, y + rr);
    c.lineTo(x + w, y + h - rr);
    c.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
    c.lineTo(x + rr, y + h);
    c.quadraticCurveTo(x, y + h, x, y + h - rr);
    c.lineTo(x, y + rr);
    c.quadraticCurveTo(x, y, x + rr, y);
  };

  ctx.beginPath();
  ctx.arc(headCX, headCY, hr, 0, Math.PI * 2);
  ctx.stroke();

  // Nose indicator — always on the right side of the circle, shows user to face right.
  const noseTipX  = headCX + hr + 28 * sy;
  const noseTipY  = headCY;
  const noseBaseX = headCX + hr - 6 * sy;
  const noseHalfW = 16 * sy;
  ctx.beginPath();
  ctx.moveTo(noseBaseX, noseTipY - noseHalfW);
  ctx.lineTo(noseTipX, noseTipY);
  ctx.lineTo(noseBaseX, noseTipY + noseHalfW);
  ctx.stroke();

  const bodyW = 145 * sx;
  // Body is centred on the spine (mid-shoulder), which sits behind/left of the nose.
  // Fall back to a fixed 40-px offset when no spine position is provided.
  const bodyCenterX = spineX ?? headX - 40 * sy;
  const bodyX = bodyCenterX - bodyW / 2;
  // data: shoulderBelowAnchor min=60*sy, mean=73*sy.
  // Keep total bodyY offset = anchorY + 50*sy so shoulders stay inside.
  // hr grew to 46 so neckGap = 50 - 46 = 4*sy.
  const bodyY = headCY + hr + 4 * sy;
  const bodyR = 26 * sy;

  // Torso rectangle (shoulders to waist). data: torsoH mean=201*sy.
  const torsoH = 201 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, bodyX, bodyY, bodyW, torsoH, bodyR);
  ctx.stroke();

  // Leg rectangle — slightly narrower, sits directly below torso. data: legH mean=291*sy.
  const legW = bodyW * 0.78;
  const legH = 291 * sy;
  const legX = bodyCenterX - legW / 2;
  const legY = bodyY + torsoH + 6 * sy;
  const legR = 20 * sy;
  ctx.beginPath();
  roundedRectPath(ctx, legX, legY, legW, legH, legR);
  ctx.stroke();

  // Visible arm — shoulder to fingertip. data: shoulder→wrist=211*sy, hand≈50*sy extra.
  const armW = 62 * sx;
  const armH = 261 * sy;
  const armX = bodyCenterX - armW / 2;
  const armY = bodyY;
  const armR = 16 * sy;

  ctx.beginPath();
  roundedRectPath(ctx, armX, armY, armW, armH, armR);
  ctx.stroke();
}

function drawGuidancePrototype(
  ctx: CanvasRenderingContext2D,
  kpPx: [number, number][],
  view: "front" | "right",
  natW: number,
  natH: number,
  poseOk: boolean,
  headRadiusMult: number = 1,
  torsoWidthMult: number = 1,
  sideFatnessMult: number = 1
) {
  // --- guidance box ---
  const marginH = 0.05;
  const marginW = view === "front" ? 0.05 : 0.25;
  const boxX = marginW * natW;
  const boxY = marginH * natH;
  const boxW = (1 - marginW * 2) * natW;
  const boxH = (1 - marginH * 2) * natH;
  const inBox = keypointsInsideGuidanceBox(kpPx, natW, natH, view);
  const boxColor = inBox && poseOk ? "rgba(34,197,94,0.85)" : "rgba(239,68,68,0.85)";
  ctx.strokeStyle = boxColor;
  ctx.lineWidth = 10;
  ctx.strokeRect(boxX, boxY, boxW, boxH);

  // --- figure height in drawing units at scale=1 ---
  // Front: head-top(46) + neck(18) + torso(214) + gap(6) + legs(321) = 605 below anchor → total 651
  // Side:  head-top(46) + neck(4)  + torso(201) + gap(6) + legs(291) = 502 below anchor → total 548+46=594
  const FIG_H        = view === "front" ? 651 : 594;
  const FIG_HEAD_TOP = 46; // head-circle radius at scale=1, headRadiusMult=1

  // Scale so the figure covers 50–90% of the canvas height (prefer 90%).
  const scaleMax = 0.90 * natH / FIG_H;
  const scaleMin = 0.50 * natH / FIG_H;

  // Head-anchor Y that centres the figure vertically for a given scale.
  const centeredHeadY = (s: number) =>
    natH / 2 - (FIG_H / 2 - FIG_HEAD_TOP) * s;

  // --- check full body visible (key joints not clipped) ---
  const coreIdx   = [0, 5, 6, 11, 12, 15, 16]; // nose, shoulders, hips, ankles
  const clipMargin = 0.03;
  const bodyDetected = coreIdx.every(i => {
    const p = kpPx[i];
    return Array.isArray(p)
      && Number.isFinite(p[0]) && Number.isFinite(p[1])
      && p[0] > clipMargin * natW && p[0] < (1 - clipMargin) * natW
      && p[1] > clipMargin * natH && p[1] < (1 - clipMargin) * natH;
  });

  // --- raw pose-derived values ---
  const [anchorX, anchorY] = getHeadAnchor(kpPx);
  const bbox           = getPoseBBox(kpPx);
  const outlineTargetH = bbox.height * (view === "right" ? 1.26 : 1.16);
  const rawScale       = outlineTargetH / FIG_H;

  // --- resolve final headX / headY / scale ---
  let headX: number, headY: number, scale: number, message: string | null = null;
  const cx = natW / 2;

  if (!bodyDetected) {
    // Ghost figure: centred, 90 % size; show a hint.
    scale  = scaleMax;
    headX  = cx;
    headY  = centeredHeadY(scale);
    message = rawScale > scaleMax ? "Step back" : "Stay in center";
  } else {
    // Track person but clamp: ±25 % of canvas from centre, scale in [50 %, 90 %].
    scale         = Math.max(scaleMin, Math.min(scaleMax, rawScale));
    const cy      = centeredHeadY(scale);
    headX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, anchorX));
    headY = Math.max(cy - 0.25 * natH, Math.min(cy + 0.25 * natH, anchorY));
  }

  const silhouetteColor = poseOk ? "rgba(34,197,94,0.95)" : "rgba(245,158,11,0.95)";

  if (view === "right") {
    let spineX: number | undefined;
    if (bodyDetected) {
      const s5 = kpPx[5]; const s6 = kpPx[6];
      if (s5 && s6 && Number.isFinite(s5[0]) && Number.isFinite(s6[0])) {
        const raw = (s5[0] + s6[0]) / 2;
        spineX = Math.max(cx - 0.25 * natW, Math.min(cx + 0.25 * natW, raw));
      }
    }
    drawSideSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, sideFatnessMult, spineX);
  } else {
    drawFrontSilhouette(ctx, headX, headY, scale, silhouetteColor, headRadiusMult, torsoWidthMult);
  }

  // --- overlay message when body is not fully in frame ---
  if (message) {
    const fontSize = Math.round(natH * 0.045);
    ctx.save();
    ctx.font           = `bold ${fontSize}px sans-serif`;
    ctx.textAlign      = "center";
    ctx.textBaseline   = "middle";
    ctx.lineWidth      = fontSize * 0.15;
    ctx.strokeStyle    = "rgba(0,0,0,0.7)";
    ctx.fillStyle      = "rgba(239,68,68,1)";
    ctx.strokeText(message, natW / 2, natH * 0.06);
    ctx.fillText(message,   natW / 2, natH * 0.06);
    ctx.restore();
  }
}

function connectionOk(
  a: number,
  b: number,
  c: CheckResults,
  view: "front" | "right"
): boolean {
  const edge = `${Math.min(a, b)}-${Math.max(a, b)}`;
  switch (edge) {
    case "6-8":  return c.rArmAngle && c.armInside;
    case "8-10": return c.rElbow    && c.armInside;
    case "5-7":  return view === "right" ? true : c.lArmAngle;
    case "7-9":  return view === "right" ? true : c.lElbow;
    case "12-14": return c.rLegAngle;
    case "14-16": return c.rLegAngle;
    case "11-13": return view === "right" ? true : c.lLegAngle;
    case "13-15": return view === "right" ? true : c.lLegAngle;
    case "5-11": case "6-12": case "5-6": case "11-12": return c.torso;
    default: return true;
  }
}

function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  kpPx: [number, number][],
  checks: CheckResults,
  view: "front" | "right",
  natW: number,
  natH: number,
  showGuidancePrototype: boolean,
  headRadiusMult: number = 1,
  torsoWidthMult: number = 1,
  sideFatnessMult: number = 1
) {
  ctx.clearRect(0, 0, natW, natH);

  const poseOk = Object.values(checks).every(Boolean);
  if (showGuidancePrototype) {
    drawGuidancePrototype(ctx, kpPx, view, natW, natH, poseOk, headRadiusMult, torsoWidthMult, sideFatnessMult);
  }

  COCO_SKELETON.forEach(([a, b]) => {
    const [ax, ay] = kpPx[a];
    const [bx, by] = kpPx[b];
    const ok = connectionOk(a, b, checks, view);
    ctx.strokeStyle = ok ? "rgba(34,197,94,0.9)" : "rgba(239,68,68,0.9)";
    ctx.lineWidth = 10;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
    ctx.stroke();
  });

  kpPx.forEach(([px, py]) => {
    ctx.fillStyle = "rgba(250,204,21,0.95)";
    ctx.beginPath();
    ctx.arc(px, py, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.5)";
    ctx.lineWidth = 3;
    ctx.stroke();
  });
}

// ---------------------------------------------------------------------------
// Category styles
// ---------------------------------------------------------------------------
const CATEGORY_STYLE: Record<Category, { label: string; bg: string; text: string }> = {
  inliers_sample:    { label: "Inlier",  bg: "bg-green-100",  text: "text-green-800" },
  outliers_sample:   { label: "Outlier", bg: "bg-red-100",    text: "text-red-800" },
  fe_false_positive: { label: "FE-FP",   bg: "bg-orange-100", text: "text-orange-800" },
  fe_false_negative: { label: "FE-FN",   bg: "bg-blue-100",   text: "text-blue-800" },
};

// ---------------------------------------------------------------------------
// PoseCard
// ---------------------------------------------------------------------------
interface PoseCardProps {
  sample: PoseSample;
  category?: Category;
  rotData?: RotationData;
  /** Pre-classified errors (so grouped view highlights consistently) */
  errors?: PoseError[];
  /** Optional: highlight a specific error category badge */
  highlightError?: PoseErrorId;
  showGuidancePrototype?: boolean;
  onExpandClick?: (key: string, canvas: HTMLCanvasElement) => void;
}

const PoseCard: React.FC<PoseCardProps> = ({
  sample,
  category,
  rotData,
  errors,
  highlightError,
  showGuidancePrototype = true,
  onExpandClick,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Track actual image dimensions so the canvas resolution matches coco_kp_px space
  const [imgNat, setImgNat] = useState({ w: IMG_W, h: IMG_H });
  const [imgLoaded, setImgLoaded] = useState(false);

  const viewFile = sample.view === "front" ? "front_img" : "right_img";
  const imgUrl = `/pose_images/${sample.uuid}_${viewFile}.jpeg`;

  // Use real pixel coords for V2 checks — bbox-norm coords distort narrow
  // side-view bboxes and produce wrong angles.
  const checks = useMemo(() => {
    const lms = cocoKpPxToMpLandmarks(sample.coco_kp_px as [number, number][]);
    return sample.view === "right"
      ? checkSidePoseV2(lms)
      : checkFrontPoseV2(lms);
  }, [sample]);
  const v2ok = Object.values(checks).every(Boolean);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    drawSkeleton(
      ctx,
      sample.coco_kp_px,
      checks,
      sample.view,
      imgNat.w,
      imgNat.h,
      showGuidancePrototype,
      (sample as any).__calibHeadRadius ?? 1,
      (sample as any).__calibTorsoWidth ?? 1,
      (sample as any).__calibSideFatness ?? 1
    );
  }, [sample, checks, imgNat, showGuidancePrototype]);

  useEffect(() => {
    if (imgLoaded) drawCanvas();
  }, [imgLoaded, drawCanvas]);

  const cat = category ? CATEGORY_STYLE[category] : null;

  const pill = (ok: boolean, label: string) => (
    <span
      className={`inline-block px-1.5 py-0.5 rounded text-xs font-mono ${
        ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {label}: {ok ? "✓" : "✗"}
    </span>
  );

  const metric = (label: string, val: number, ok: boolean) => (
    <div className={`text-xs font-mono ${ok ? "text-gray-500" : "text-red-600 font-semibold"}`}>
      {label}: {val.toFixed(1)}°
    </div>
  );

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-2 py-1 border-b border-gray-100 bg-gray-50">
        {cat && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${cat.bg} ${cat.text}`}>
            {cat.label}
          </span>
        )}
        <span className="text-xs text-gray-400 truncate">{sample.uuid.slice(0, 8)}</span>
      </div>

      {/* Image + skeleton overlay.
           Container aspect-ratio updates after load so it always matches
           the actual image dimensions (some samples are 4000×3000). */}
      <div
        className="relative bg-gray-900 cursor-pointer hover:opacity-90 transition-opacity"
        style={{ aspectRatio: `${imgNat.w}/${imgNat.h}` }}
        onClick={() => {
          if (canvasRef.current && onExpandClick) {
            onExpandClick(sample.uuid + sample.view, canvasRef.current);
          }
        }}
      >
        <img
          src={imgUrl}
          alt={`${sample.view} pose`}
          className="absolute inset-0 w-full h-full object-fill"
          onLoad={(e) => {
            setImgNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight });
            setImgLoaded(true);
          }}
          onError={() => setImgLoaded(true)}
        />
        <canvas
          ref={canvasRef}
          width={imgNat.w}
          height={imgNat.h}
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: "none" }}
        />
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-1 px-2 pt-1.5">
        {pill(v2ok,                     "V2")}
        {pill(sample.fe_valid,          "FE")}
        {pill(sample.pipeline_be_valid, "BE")}
      </div>

      {/* Metrics */}
      <div className="px-2 pb-2 pt-1 space-y-0.5">
        {metric("L arm",   sample.l_arm_vert,     checks.lArmAngle)}
        {metric("R arm",   sample.r_arm_vert,     checks.rArmAngle)}
        {metric("L elbow", sample.l_elbow_ang,    checks.lElbow)}
        {metric("R elbow", sample.r_elbow_ang,    checks.rElbow)}
        {metric("L leg",   sample.l_leg_vert,     checks.lLegAngle)}
        {metric("R leg",   sample.r_leg_vert,     checks.rLegAngle)}
        {metric("Torso",   sample.torso_tilt_deg, checks.torso)}
        {rotData?.body_rotation_deg !== undefined && (
          <div className={`text-xs font-mono ${
            sample.view === "front" && rotData.body_rotation_deg > 7.5
              ? "text-red-600 font-semibold" : "text-gray-500"
          }`}>
            Rotation: {rotData.body_rotation_deg.toFixed(1)}°
          </div>
        )}
        {sample.camera_elevation_m !== undefined && (
          <div className={`text-xs font-mono ${
            sample.camera_elevation_m < (sample.view === "right" ? -0.35 : -0.5)
              ? "text-red-600 font-semibold"
              : sample.camera_elevation_m > (sample.view === "right" ? 0.2 : 0.15)
                ? "text-orange-500 font-semibold"
                : "text-gray-500"
          }`}>
            Cam elev: {sample.camera_elevation_m.toFixed(3)}&nbsp;m
          </div>
        )}
        {sample.view === "right" && sample.side_arm && (
          <div className={`text-xs font-mono ${checks.armInside ? "text-gray-500" : "text-red-600 font-semibold"}`}>
            dxWr: {sample.side_arm.dx_wrist_over_trunk.toFixed(2)}{" "}
            dxEl: {sample.side_arm.dx_elbow_over_trunk.toFixed(2)}
          </div>
        )}
        {!v2ok && (
          <div className="text-xs text-red-500 pt-0.5">
            {[
              !checks.lArmAngle && "L arm",
              !checks.rArmAngle && "R arm",
              !checks.lElbow    && "L elbow",
              !checks.rElbow    && "R elbow",
              !checks.lLegAngle && "L leg",
              !checks.rLegAngle && "R leg",
              !checks.torso     && "Torso",
              !checks.armInside && "Arm inside",
            ].filter(Boolean).join(", ")}
          </div>
        )}
        {/* Semantic error pills */}
        {errors && errors.length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1.5 border-t border-gray-100 mt-1">
            {errors.map((e) => (
              <span
                key={e.id}
                title={e.message}
                className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono ${
                  e.id === highlightError
                    ? "bg-yellow-200 text-yellow-900 ring-1 ring-yellow-500"
                    : e.severity === "error"
                    ? "bg-red-100 text-red-700"
                    : "bg-amber-100 text-amber-800"
                }`}
              >
                {e.label}
              </span>
            ))}
          </div>
        )}
        {errors && errors.length === 0 && (
          <div className="text-xs text-green-600 font-semibold pt-1">✓ No issues</div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
const VIEWS = ["front", "right"] as const;
const CATEGORIES: Category[] = [
  "inliers_sample",
  "outliers_sample",
  "fe_false_positive",
  "fe_false_negative",
];

const PoseDebugPage: React.FC = () => {
  const [data, setData] = useState<SamplesJson | null>(null);
  const [catalog, setCatalog] = useState<Record<string, PoseSample[]> | null>(null);
  const [rotations, setRotations] = useState<Record<string, RotationData>>({});
  const [imgDims, setImgDims] = useState<Record<string, { w: number; h: number; exif?: number; raw_w?: number; raw_h?: number }>>({}); 
  const [view, setView] = useState<"front" | "right">("front");
  const [mode, setMode] = useState<"category" | "errors" | "voice">("errors");
  const [showGuidancePrototype, setShowGuidancePrototype] = useState(true);
  const [showCalibration, setShowCalibration] = useState(false);
  const [headRadiusMult, setHeadRadiusMult] = useState(1);
  const [torsoWidthMult, setTorsoWidthMult] = useState(1);
  const [sideFatnessMult, setSideFatnessMult] = useState(1);
  const [category, setCategory] = useState<Category>("inliers_sample");
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [expandedCanvas, setExpandedCanvas] = useState<HTMLCanvasElement | null>(null);

  useEffect(() => {
    fetch("/pose_samples.json")
      .then((r) => r.json())
      .then(setData)
      .catch((e) => setError(String(e)));
    fetch("/pose_rotation.json")
      .then((r) => r.json())
      .then((d: Record<string, RotationData>) => setRotations(d))
      .catch(() => {});
    fetch("/pose_image_dims.json")
      .then((r) => r.json())
      .then(setImgDims)
      .catch(() => {});
    // Full catalog — loaded once for "By error type" view
    fetch("/pose_catalog.json")
      .then((r) => r.json())
      .then(setCatalog)
      .catch(() => {});
  }, []);

  // "By error type" uses the full catalog (all 698 processed samples).
  // "By category" uses the curated pose_samples.json.
  const allForView = useMemo(() => {
    if (catalog) return (catalog[view] ?? []) as PoseSample[];
    if (!data) return [] as PoseSample[];
    const seen = new Set<string>();
    const out: PoseSample[] = [];
    for (const cat of CATEGORIES) {
      for (const s of data[view][cat]) {
        const key = s.uuid + s.view;
        if (!seen.has(key)) { seen.add(key); out.push(s); }
      }
    }
    return out;
  }, [catalog, data, view]);

  // Analyse right-view keypoint distributions to calibrate silhouette geometry.
  // Filters to valid (no-issue) samples only. All measurements are normalised
  // by bbox.height so results translate directly to `* sy` drawing constants
  // via:  constant = normalised_value * (620 / 1.26)  ≈  normalised_value * 492
  useMemo(() => {
    if (view !== "right" || allForView.length === 0) return;

    type Bag = { [key: string]: number[] };
    const bags: Bag = {
      noseMidShX:    [],  // nose X − midShoulder X  (head forward offset)
      torsoH:        [],  // midHip Y − midShoulder Y
      legH:          [],  // midAnkle Y − midHip Y
      armStartOff:   [],  // rightShoulder Y − midShoulder Y  (≈0, sanity check)
      armLength:     [],  // rightWrist Y − rightShoulder Y
      armEndOff:     [],  // rightWrist Y − midShoulder Y  (total arm+shoulder drop)
      bodyDepthX:    [],  // max X spread of torso kps (side depth proxy)
    };

    const pct = (arr: number[], p: number) => {
      const s = [...arr].sort((a, b) => a - b);
      return s[Math.floor(s.length * p)];
    };
    const fmt = (v: number | undefined) => v != null ? v.toFixed(3) : "n/a";

    for (const s of allForView) {
      // Only inliers (no pipeline issues).
      if (!s.our_be_valid) continue;
      const kp = s.coco_kp_px;
      const ok = (p: [number, number] | undefined) =>
        Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]);

      const nose = kp[0], sh5 = kp[5], sh6 = kp[6];
      const hp11 = kp[11], hp12 = kp[12];
      const an15 = kp[15], an16 = kp[16];
      const el8 = kp[8], wr10 = kp[10]; // right elbow / wrist

      if (!ok(sh5) || !ok(sh6)) continue;
      const midShX = (sh5[0] + sh6[0]) / 2;
      const midShY = (sh5[1] + sh6[1]) / 2;

      // Bounding box height (same as getPoseBBox logic subset).
      if (!ok(hp11) || !ok(hp12) || !ok(an15) || !ok(an16)) continue;
      const midHipY  = (hp11[1] + hp12[1]) / 2;
      const midAnkY  = (an15[1] + an16[1]) / 2;
      const bboxH = midAnkY - midShY;
      if (bboxH < 10) continue;

      if (ok(nose)) bags.noseMidShX.push((nose[0] - midShX) / bboxH);
      bags.torsoH.push((midHipY - midShY) / bboxH);
      bags.legH.push((midAnkY - midHipY) / bboxH);

      if (ok(sh6)) {
        const rShY = sh6[1];
        bags.armStartOff.push((rShY - midShY) / bboxH);
        if (ok(el8)) bags.armLength.push((el8[1] - rShY) / bboxH);
        if (ok(wr10)) {
          bags.armLength.push((wr10[1] - rShY) / bboxH);
          bags.armEndOff.push((wr10[1] - midShY) / bboxH);
        }
      }

      // Side body depth: x-spread of shoulders + hips.
      const xs = [sh5[0], sh6[0], hp11[0], hp12[0]].filter(Number.isFinite);
      if (xs.length >= 2) bags.bodyDepthX.push((Math.max(...xs) - Math.min(...xs)) / bboxH);
    }

    // Scale factor: constant = norm * (620 / 1.26) ≈ 492
    const K = 620 / 1.26;
    const row = (label: string, arr: number[]) => {
      if (arr.length === 0) return;
      const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
      const p25 = pct(arr, 0.25); const p75 = pct(arr, 0.75);
      console.log(
        `[SideKP] ${label.padEnd(14)} n=${arr.length}` +
        `  mean=${fmt(mean)} (→${(mean * K).toFixed(0)}*sy)` +
        `  p25=${fmt(p25)} p75=${fmt(p75)}` +
        `  min=${fmt(pct(arr, 0))} max=${fmt(pct(arr, 1))}`
      );
    };

    console.groupCollapsed("[SideView] Keypoint distribution (valid inliers, right view)");
    row("noseMidShX",   bags.noseMidShX);
    row("torsoH",       bags.torsoH);
    row("legH",         bags.legH);
    row("armStartOff",  bags.armStartOff);
    row("armLength",    bags.armLength);
    row("armEndOff",    bags.armEndOff);
    row("bodyDepthX",   bags.bodyDepthX);
    console.groupEnd();
  }, [view, allForView]);

  const errorsBySample = useMemo(() => {
    const m = new Map<string, PoseError[]>();
    for (const s of allForView) {
      const dims = imgDims[`${s.uuid}_${s.view}`];
      if (!dims) continue;
      const rot = rotations[`${s.uuid}_${s.view}`];

      // For EXIF-rotated images the stored keypoints are in raw pixel space.
      // Transform them to the display (EXIF-corrected) coordinate space so
      // framing / anatomy checks operate on the visually-correct coordinates.
      const exif = dims.exif ?? 1;
      const rawW = dims.raw_w ?? dims.w;
      const rawH = dims.raw_h ?? dims.h;
      let kp = s.coco_kp_px as [number, number][];
      if (exif === 6) {
        // 90° CW: x_disp = rawH − y_raw, y_disp = x_raw
        kp = kp.map(([x, y]) => [rawH - y, x]);
      } else if (exif === 8) {
        // 90° CCW: x_disp = y_raw, y_disp = rawW − x_raw
        kp = kp.map(([x, y]) => [y, rawW - x]);
      } else if (exif === 3) {
        // 180°: x_disp = rawW − x_raw, y_disp = rawH − y_raw
        kp = kp.map(([x, y]) => [rawW - x, rawH - y]);
      }

      // torso_tilt_deg in the JSON was computed in raw image space; for
      // EXIF-rotated images it is garbage (reads as ~90°), so skip it.
      const torsoTiltDeg = exif === 1 ? s.torso_tilt_deg : undefined;

      m.set(s.uuid + s.view, classifyPoseErrors({
        cocoKpPx: kp,
        view: s.view,
        imageWidth: dims.w,
        imageHeight: dims.h,
        lElbowAng: s.l_elbow_ang,
        rElbowAng: s.r_elbow_ang,
        torsoTiltDeg,
        bodyRotationDeg: rot?.body_rotation_deg,
        cameraElevationM: (s as any).camera_elevation_m,
      }));
    }
    return m;
  }, [allForView, imgDims, rotations]);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && expandedKey) {
        setExpandedKey(null);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [expandedKey]);

  if (error) return <div className="p-4 text-red-600">Failed to load: {error}</div>;
  if (!data)  return <div className="p-4 text-gray-500">Loading pose samples…</div>;

  const samples = data[view][category];

  const v2Pass = samples.filter((s) => {
    const lm = cocoKpPxToMpLandmarks(s.coco_kp_px as [number, number][]);
    const c = s.view === "right" ? checkSidePoseV2(lm) : checkFrontPoseV2(lm);
    return Object.values(c).every(Boolean);
  }).length;
  const fePass = samples.filter((s) => s.fe_valid).length;
  const bePass = samples.filter((s) => s.pipeline_be_valid).length;

  return (
    <IonPage>
      <IonContent>
    <div className="bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-xl font-bold mb-1">Pose Checker Debug</h1>
        <p className="text-sm text-gray-500 mb-4">
          Real photos with COCO-17 skeleton overlaid. Limbs coloured by{" "}
          <strong>PoseCheckerV2</strong> (green = pass, red = fail).
        </p>

        {/* Controls */}
        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex gap-1">
            {VIEWS.map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`px-3 py-1 rounded text-sm font-medium border ${
                  view === v
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
              >
                {v === "front" ? "Front" : "Side (right)"}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {(["category", "errors", "voice"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`px-3 py-1 rounded text-sm font-medium border ${
                  mode === m
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
              >
                {m === "category" ? "By category" : m === "errors" ? "By error type" : "By voice message"}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowGuidancePrototype((v) => !v)}
            className={`px-3 py-1 rounded text-sm font-medium border ${
              showGuidancePrototype
                ? "bg-emerald-700 text-white border-emerald-700"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            {showGuidancePrototype ? "Prototype ON" : "Prototype OFF"}
          </button>
          <button
            onClick={() => setShowCalibration((v) => !v)}
            className={`px-3 py-1 rounded text-sm font-medium border ${
              showCalibration
                ? "bg-blue-700 text-white border-blue-700"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
            }`}
          >
            {showCalibration ? "Calibration ON" : "Calibration OFF"}
          </button>
          {showCalibration && (
            <div className="border border-blue-300 rounded p-4 bg-blue-50">
              <div className="font-semibold text-sm mb-3">Calibration Parameters</div>
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1">
                    Head Radius: {headRadiusMult.toFixed(2)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={headRadiusMult}
                    onChange={(e) => setHeadRadiusMult(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">
                    Torso Width: {torsoWidthMult.toFixed(2)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={torsoWidthMult}
                    onChange={(e) => setTorsoWidthMult(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">
                    Side Fatness: {sideFatnessMult.toFixed(2)}x
                  </label>
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.05"
                    value={sideFatnessMult}
                    onChange={(e) => setSideFatnessMult(parseFloat(e.target.value))}
                    className="w-full"
                  />
                </div>
                <button
                  onClick={() => {
                    const code = `// Silhouette calibration constants
const HEAD_RADIUS_MULT_FRONT = ${headRadiusMult};
const HEAD_RADIUS_MULT_SIDE = ${headRadiusMult};
const TORSO_WIDTH_MULT = ${torsoWidthMult};
const SIDE_FATNESS_MULT = ${sideFatnessMult};`;
                    navigator.clipboard.writeText(code);
                    alert("Calibration constants copied to clipboard!");
                  }}
                  className="px-3 py-2 bg-blue-600 text-white text-xs rounded font-medium hover:bg-blue-700"
                >
                  Export Constants
                </button>
              </div>
            </div>
          )}
          {mode === "category" && (
            <div className="flex gap-1 flex-wrap">
              {CATEGORIES.map((cat) => {
                const s = CATEGORY_STYLE[cat];
                return (
                  <button
                    key={cat}
                    onClick={() => setCategory(cat)}
                    className={`px-3 py-1 rounded text-sm font-medium border ${
                      category === cat
                        ? `${s.bg} ${s.text} border-current`
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {mode === "category" ? (
          <>
            {/* Pass-rate summary */}
            <div className="flex gap-6 mb-4 text-sm font-medium">
              <span>
                V2:{" "}
                <span className={v2Pass === samples.length ? "text-green-600" : "text-orange-600"}>
                  {v2Pass}/{samples.length}
                </span>
              </span>
              <span>
                Legacy FE:{" "}
                <span className={fePass === samples.length ? "text-green-600" : "text-orange-600"}>
                  {fePass}/{samples.length}
                </span>
              </span>
              <span>
                Backend:{" "}
                <span className={bePass === samples.length ? "text-green-600" : "text-orange-600"}>
                  {bePass}/{samples.length}
                </span>
              </span>
            </div>

            {/* Grid */}
            <div
              className="grid gap-4"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
            >
              {samples.map((s) => (
                <PoseCard
                  key={s.uuid + s.view}
                  sample={s}
                  category={category}
                  rotData={rotations[`${s.uuid}_${s.view}`]}
                  errors={errorsBySample.get(s.uuid + s.view)}
                  showGuidancePrototype={showGuidancePrototype}
                  onExpandClick={(key, canvas) => {
                    setExpandedKey(key);
                    setExpandedCanvas(canvas);
                  }}
                />
              ))}
            </div>
          </>
        ) : mode === "errors" ? (
          <ErrorGroupedView
            samples={allForView}
            errorsBySample={errorsBySample}
            rotations={rotations}
            showGuidancePrototype={showGuidancePrototype}
            onExpandClick={(key, canvas) => {
              setExpandedKey(key);
              setExpandedCanvas(canvas);
            }}
          />
        ) : (
          <VoiceGroupedView
            samples={allForView}
            errorsBySample={errorsBySample}
            rotations={rotations}
            showGuidancePrototype={showGuidancePrototype}
            onExpandClick={(key, canvas) => {
              setExpandedKey(key);
              setExpandedCanvas(canvas);
            }}
          />
        )}

      {/* Expanded image modal */}
      {expandedKey && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4"
          onClick={() => setExpandedKey(null)}
        >
          <div
            className="bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <span className="font-semibold text-gray-700">Expanded View</span>
              <button
                onClick={() => setExpandedKey(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="overflow-auto flex-1 bg-gray-900 flex items-center justify-center">
              {expandedCanvas && (
                <canvas
                  width={expandedCanvas.width}
                  height={expandedCanvas.height}
                  style={{ maxWidth: "100%", maxHeight: "100%", display: "block" }}
                  ref={(canvasEl) => {
                    if (canvasEl && expandedCanvas) {
                      const ctx = canvasEl.getContext("2d");
                      if (ctx) {
                        ctx.drawImage(expandedCanvas, 0, 0);
                      }
                    }
                  }}
                />
              )}
            </div>
            <div className="bg-gray-100 px-4 py-2 border-t border-gray-200 text-sm text-gray-600">
              Click image or press Escape to close
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
      </IonContent>
    </IonPage>
  );
};

// ---------------------------------------------------------------------------
// ErrorGroupedView — shows N samples per error category + "No issues" group
// ---------------------------------------------------------------------------
interface ErrorGroupedViewProps {
  samples: PoseSample[];
  errorsBySample: Map<string, PoseError[]>;
  rotations: Record<string, RotationData>;
  showGuidancePrototype: boolean;
  onExpandClick?: (key: string, canvas: HTMLCanvasElement) => void;
}

const MAX_PER_GROUP = 24;

const ErrorGroupedView: React.FC<ErrorGroupedViewProps> = ({
  samples,
  errorsBySample,
  rotations,
  showGuidancePrototype,
  onExpandClick,
}) => {
  // All groups that actually have samples — computed first so we can init filter state
  const allGroupIds = useMemo(() => {
    const ids: Array<"__clean__" | PoseErrorId> = [];
    const cleanCount = samples.filter((s) => errorsBySample.get(s.uuid + s.view)?.length === 0).length;
    if (cleanCount > 0) ids.push("__clean__");
    for (const id of ERROR_CATEGORY_ORDER) {
      const total = samples.filter((s) => errorsBySample.get(s.uuid + s.view)?.some((e) => e.id === id)).length;
      if (total > 0) ids.push(id);
    }
    return ids;
  }, [samples, errorsBySample]);

  const [visibleGroups, setVisibleGroups] = useState<Set<string>>(() => new Set());
  // Sync visible groups when allGroupIds changes (e.g. view switch)
  const prevGroupsRef = React.useRef<string>("");
  const groupKey = allGroupIds.join(",");
  if (prevGroupsRef.current !== groupKey) {
    prevGroupsRef.current = groupKey;
    // eslint-disable-next-line react-hooks/rules-of-hooks -- safe: sync update before render
    // Reset to show all
    if (visibleGroups.size === 0) {
      // initial load — show all
    }
  }
  const effectiveVisible = visibleGroups.size === 0
    ? new Set(allGroupIds.map(String))
    : visibleGroups.has("__none__")
    ? new Set<string>()
    : visibleGroups;

  const toggleGroup = (id: string) => {
    setVisibleGroups((prev) => {
      // "None" sentinel — start from empty, add just this one
      if (prev.has("__none__")) return new Set([id]);
      const base = prev.size === 0 ? new Set(allGroupIds.map(String)) : new Set(prev);
      if (base.has(id)) { base.delete(id); } else { base.add(id); }
      // All selected again → reset to show-all sentinel
      if (base.size === allGroupIds.length) return new Set();
      // None left → use none sentinel
      if (base.size === 0) return new Set(["__none__"]);
      return base;
    });
  };

  // Build: for each error id → samples that have it
  const byError = useMemo(() => {
    const map = new Map<PoseErrorId, PoseSample[]>();
    for (const id of ERROR_CATEGORY_ORDER) map.set(id, []);
    for (const s of samples) {
      const errs = errorsBySample.get(s.uuid + s.view);
      if (!errs) continue;
      for (const e of errs) {
        const bucket = map.get(e.id);
        if (bucket && bucket.length < MAX_PER_GROUP) bucket.push(s);
      }
    }
    return map;
  }, [samples, errorsBySample]);

  const clean = useMemo(
    () =>
      samples.filter((s) => {
        const errs = errorsBySample.get(s.uuid + s.view);
        return errs !== undefined && errs.length === 0;
      }),
    [samples, errorsBySample]
  );

  return (
    <div className="space-y-6">
      {/* Filter toggle bar */}
      <div className="flex flex-wrap gap-1.5 pb-2 border-b border-gray-200">
        <span className="text-xs text-gray-400 self-center mr-1">Show:</span>
        <button
          onClick={() => setVisibleGroups(new Set())}
          className="px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-300 bg-white text-gray-500 hover:bg-gray-100"
        >
          All
        </button>
        <button
          onClick={() => setVisibleGroups(new Set(["__none__"]))}
          className="px-2.5 py-0.5 rounded-full text-xs font-medium border border-gray-300 bg-white text-gray-500 hover:bg-gray-100"
        >
          None
        </button>
        <span className="text-gray-200 self-center">|</span>
        {allGroupIds.map((id) => {
          const isClean = id === "__clean__";
          const label = isClean ? "✓ No issues" : ERROR_CATEGORY_META[id as PoseErrorId].title;
          const active = effectiveVisible.has(String(id));
          return (
            <button
              key={id}
              onClick={() => toggleGroup(String(id))}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                active
                  ? isClean
                    ? "bg-green-600 text-white border-green-600"
                    : "bg-gray-800 text-white border-gray-800"
                  : "bg-white text-gray-400 border-gray-300 hover:border-gray-500 hover:text-gray-600"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Clean samples */}
      {effectiveVisible.has("__clean__") && (
      <ErrorGroupSection
        title="✓ No issues detected"
        description="Samples with no semantic errors — these should always pass."
        samples={clean}
        count={samples.filter((s) => errorsBySample.get(s.uuid + s.view)?.length === 0).length}
        tone="ok"
        errorsBySample={errorsBySample}
        rotations={rotations}
        showGuidancePrototype={showGuidancePrototype}
        onExpandClick={onExpandClick}
      />
      )}

      {ERROR_CATEGORY_ORDER.map((id) => {
        const meta = ERROR_CATEGORY_META[id];
        const group = byError.get(id) || [];
        const total = samples.filter((s) =>
          errorsBySample.get(s.uuid + s.view)?.some((e) => e.id === id)
        ).length;
        if (total === 0) return null;
        if (!effectiveVisible.has(id)) return null;
        return (
          <ErrorGroupSection
            key={id}
            title={meta.title}
            description={meta.description}
            samples={group}
            count={total}
            tone="err"
            highlight={id}
            errorsBySample={errorsBySample}
            rotations={rotations}
            showGuidancePrototype={showGuidancePrototype}
            onExpandClick={onExpandClick}
          />
        );
      })}
    </div>
  );
};

interface ErrorGroupSectionProps {
  title: string;
  description: string;
  samples: PoseSample[];
  count: number;
  tone: "ok" | "err";
  highlight?: PoseErrorId;
  errorsBySample: Map<string, PoseError[]>;
  rotations: Record<string, RotationData>;
  showGuidancePrototype: boolean;
  onExpandClick?: (key: string, canvas: HTMLCanvasElement) => void;
}

const ErrorGroupSection: React.FC<ErrorGroupSectionProps> = ({
  title,
  description,
  samples,
  count,
  tone,
  highlight,
  errorsBySample,
  rotations,
  showGuidancePrototype,
  onExpandClick,
}) => (
  <section>
    <div className="flex items-baseline gap-3 mb-2">
      <h2 className={`text-base font-semibold ${tone === "ok" ? "text-green-700" : "text-gray-900"}`}>
        {title}
      </h2>
      <span className="text-sm text-gray-500">{description}</span>
      <span className="text-sm font-mono text-gray-500 ml-auto">{count} sample{count === 1 ? "" : "s"}</span>
    </div>
    {samples.length === 0 ? (
      <div className="text-sm text-gray-400 italic py-2">None</div>
    ) : (
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
      >
        {samples.map((s) => (
          <PoseCard
            key={s.uuid + s.view}
            sample={s}
            rotData={rotations[`${s.uuid}_${s.view}`]}
            errors={errorsBySample.get(s.uuid + s.view)}
            highlightError={highlight}
            showGuidancePrototype={showGuidancePrototype}
            onExpandClick={onExpandClick}
          />
        ))}
      </div>
    )}
  </section>
);

// ---------------------------------------------------------------------------
// VoiceGroupedView — one section per voice message; only highest-priority
// message is shown per sample, mirroring real runtime behaviour.
// ---------------------------------------------------------------------------
interface VoiceGroupedViewProps {
  samples: PoseSample[];
  errorsBySample: Map<string, PoseError[]>;
  rotations: Record<string, RotationData>;
  showGuidancePrototype: boolean;
  onExpandClick?: (key: string, canvas: HTMLCanvasElement) => void;
}

const VOICE_GROUP_COLORS: Record<string, { pill: string; heading: string }> = {
  wrong_orientation: { pill: "bg-red-700 text-white border-red-700",       heading: "text-red-700" },
  not_detected:      { pill: "bg-red-700 text-white border-red-700",       heading: "text-red-700" },
  step_back:         { pill: "bg-orange-600 text-white border-orange-600", heading: "text-orange-700" },
  move_to_centre:    { pill: "bg-orange-600 text-white border-orange-600", heading: "text-orange-700" },
  step_closer:       { pill: "bg-orange-600 text-white border-orange-600", heading: "text-orange-700" },
  adjust_camera:     { pill: "bg-purple-600 text-white border-purple-600", heading: "text-purple-700" },
  face_camera:       { pill: "bg-blue-600 text-white border-blue-600",     heading: "text-blue-700" },
  stand_straight:    { pill: "bg-blue-600 text-white border-blue-600",     heading: "text-blue-700" },
  feet_apart:        { pill: "bg-teal-600 text-white border-teal-600",     heading: "text-teal-700" },
  feet_together:     { pill: "bg-teal-600 text-white border-teal-600",     heading: "text-teal-700" },
  lower_arms:        { pill: "bg-indigo-600 text-white border-indigo-600", heading: "text-indigo-700" },
  straighten_arms:   { pill: "bg-indigo-600 text-white border-indigo-600", heading: "text-indigo-700" },
  arms_level:        { pill: "bg-indigo-600 text-white border-indigo-600", heading: "text-indigo-700" },
  arms_in:           { pill: "bg-indigo-600 text-white border-indigo-600", heading: "text-indigo-700" },
};

const VoiceGroupedView: React.FC<VoiceGroupedViewProps> = ({
  samples,
  errorsBySample,
  rotations,
  showGuidancePrototype,
  onExpandClick,
}) => {
  // Compute the winning voice message per sample
  const voicePerSample = useMemo(() => {
    const m = new Map<string, VoiceMessage | null>();
    for (const s of samples) {
      const errs = errorsBySample.get(s.uuid + s.view) ?? [];
      m.set(s.uuid + s.view, getVoiceMessage(errs));
    }
    return m;
  }, [samples, errorsBySample]);

  // Build groups in priority order
  const groups = useMemo(() => {
    const byId = new Map<VoiceMessageId, PoseSample[]>();
    for (const id of VOICE_MESSAGE_ORDER) byId.set(id, []);
    const clean: PoseSample[] = [];
    for (const s of samples) {
      const msg = voicePerSample.get(s.uuid + s.view);
      if (!msg) { clean.push(s); }
      else {
        const bucket = byId.get(msg.id);
        if (bucket && bucket.length < MAX_PER_GROUP) bucket.push(s);
      }
    }
    return { byId, clean };
  }, [samples, voicePerSample]);

  // Group counts (all samples, not just displayed)
  const groupCounts = useMemo(() => {
    const counts = new Map<VoiceMessageId, number>();
    for (const s of samples) {
      const msg = voicePerSample.get(s.uuid + s.view);
      if (msg) counts.set(msg.id, (counts.get(msg.id) ?? 0) + 1);
    }
    return counts;
  }, [samples, voicePerSample]);

  const cleanCount = samples.filter((s) => !voicePerSample.get(s.uuid + s.view)).length;

  // Priority group labels
  const PRIORITY_GROUPS = [
    { label: "1 · Detection failure", ids: ["wrong_orientation", "not_detected"] as VoiceMessageId[] },
    { label: "2 · Body in frame",     ids: ["step_back", "move_to_centre", "step_closer"] as VoiceMessageId[] },
    { label: "3 · Camera position",   ids: ["adjust_camera"] as VoiceMessageId[] },
    { label: "4 · Rotation / alignment", ids: ["face_camera", "stand_straight"] as VoiceMessageId[] },
    { label: "5 · Feet position",     ids: ["feet_apart", "feet_together"] as VoiceMessageId[] },
    { label: "6 · Arm position",      ids: ["lower_arms", "straighten_arms", "arms_level", "arms_in"] as VoiceMessageId[] },
  ];

  return (
    <div className="space-y-8">
      {/* Summary stats */}
      <div className="flex flex-wrap gap-3 text-sm pb-3 border-b border-gray-200">
        <span className="font-medium text-gray-600">Voice message sent to:</span>
        {VOICE_MESSAGE_ORDER.map((id) => {
          const n = groupCounts.get(id) ?? 0;
          if (n === 0) return null;
          const c = VOICE_GROUP_COLORS[id] ?? { pill: "bg-gray-700 text-white border-gray-700" };
          return (
            <span key={id} className={`px-2 py-0.5 rounded-full text-xs font-medium border ${c.pill}`}>
              {VOICE_MESSAGE_META[id].title} — {n}
            </span>
          );
        })}
        {cleanCount > 0 && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-green-600 text-white border-green-600">
            ✓ No message — {cleanCount}
          </span>
        )}
      </div>

      {/* Sections by priority group */}
      {PRIORITY_GROUPS.map((group) => {
        const groupSamples = group.ids.flatMap((id) => {
          const bucket = groups.byId.get(id) ?? [];
          return bucket.map((s) => ({ s, id }));
        });
        if (groupSamples.length === 0) return null;
        return (
          <div key={group.label}>
            <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
              {group.label}
            </h2>
            <div className="space-y-5">
              {group.ids.map((id) => {
                const bucket = groups.byId.get(id) ?? [];
                const total = groupCounts.get(id) ?? 0;
                if (total === 0) return null;
                const meta = VOICE_MESSAGE_META[id];
                const c = VOICE_GROUP_COLORS[id] ?? { pill: "bg-gray-700 text-white border-gray-700", heading: "text-gray-700" };
                return (
                  <section key={id}>
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold border ${c.pill}`}>
                        🔊 "{meta.title}"
                      </span>
                      <span className="text-xs text-gray-400">
                        triggers: {meta.triggers.join(", ")}
                      </span>
                      <span className="text-sm font-mono text-gray-500 ml-auto">
                        {total} sample{total === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div
                      className="grid gap-4"
                      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
                    >
                      {bucket.map((s) => (
                        <PoseCard
                          key={s.uuid + s.view}
                          sample={s}
                          rotData={rotations[`${s.uuid}_${s.view}`]}
                          errors={errorsBySample.get(s.uuid + s.view)}
                          showGuidancePrototype={showGuidancePrototype}
                          onExpandClick={onExpandClick}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Clean */}
      {groups.clean.length > 0 && (
        <div>
          <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">
            No message sent
          </h2>
          <div
            className="grid gap-4"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
          >
            {groups.clean.map((s) => (
              <PoseCard
                key={s.uuid + s.view}
                sample={s}
                rotData={rotations[`${s.uuid}_${s.view}`]}
                errors={errorsBySample.get(s.uuid + s.view)}
                showGuidancePrototype={showGuidancePrototype}
                onExpandClick={onExpandClick}
              />
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default PoseDebugPage;

